import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Empty, Input, List, Modal, Select, Spin, Tag } from 'antd';

import { api } from '../../api/client';
import { API_ITEMS_URL } from '../../utils';
import { normalizeId } from '../../utils/ids';
import { itemHomeCollectionId } from '../../utils/linkedRecords';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import getItemDisplayValue from '../../utils/items';
import { createLookupResolver } from '../../utils/lookups';
import useLinkedItems from '../../hooks/useLinkedItems';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import { optionSearchFilter } from '../Input';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

// The API caps a page at 200. Rather than paginate a picker, we load one full
// page and let the card-type filter narrow the query server-side when a
// collection is bigger than that.
const FETCH_LIMIT = 200;

/**
 * Picker for relating an existing item in the same collection.
 *
 * Items already related through a card-lookup element are still selectable —
 * a derived edge and a linked edge are separate records by design — but items
 * already *linked* are disabled, since the API rejects an exact duplicate.
 */
export default function RelateItemModal({
	open,
	onCancel,
	onSelect,
	collectionId,
	excludeItemId,
	linkedItemIds,
	saving = false,
}) {
	const { cardsById, getElementsForCard, loading: cardsLoading } = useCardDefinitions();

	const [items, setItems] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [loading, setLoading] = useState(false);
	// A read has answered; until then the list is a skeleton, and a failed read is an error with Retry, never "no items".
	const [loaded, setLoaded] = useState(false);
	const [loadError, setLoadError] = useState(false);
	const [attempt, setAttempt] = useState(0);
	const [cardFilter, setCardFilter] = useState(null);
	const [search, setSearch] = useState('');

	useEffect(() => {
		if (open) return;
		setSearch('');
		setCardFilter(null);
		setLoaded(false);
		setLoadError(false);
	}, [open]);

	useEffect(() => {
		if (!open || !collectionId) return undefined;

		let active = true;
		const params = { collection_id: collectionId, limit: FETCH_LIMIT };
		if (cardFilter) params.card_id = cardFilter;

		setLoading(true);
		setLoadError(false);
		api.get(API_ITEMS_URL, { params })
			.then(({ data, headers }) => {
				if (!active) return;
				const page = Array.isArray(data) ? data : [];
				setItems(page);
				const parsed = Number(headers?.['x-total-count']);
				setTotalCount(Number.isFinite(parsed) && parsed >= 0 ? parsed : page.length);
				setLoaded(true);
			})
			.catch(() => {
				if (!active) return;
				setLoadError(true);
			})
			.finally(() => {
				if (active) setLoading(false);
			});

		return () => { active = false; };
	}, [open, collectionId, cardFilter, attempt]);

	// A candidate's display template can nest a lookup pointing at an item outside
	// this page (or outside the card-type filter), so the page seeds the pool and
	// the missing referenced items are batch-fetched rather than left as raw ids.
	const itemsById = useLinkedItems({
		items,
		getElementsForCard,
		enabled: open && !cardsLoading,
		resetKey: collectionId,
	});

	const resolveLookupValue = useMemo(() => createLookupResolver({
		itemsById,
		cardsById,
		getElementsForCard,
	}), [itemsById, cardsById, getElementsForCard]);

	const excludedId = normalizeId(excludeItemId);
	const linkedSet = useMemo(() => new Set(linkedItemIds || []), [linkedItemIds]);

	const rows = useMemo(() => {
		const needle = search.trim().toLowerCase();
		return items
			.map(item => {
				const id = normalizeId(item?._id);
				if (!id || id === excludedId) return null;
				const card = cardsById[normalizeId(item?.card_id)];
				const label = getItemDisplayValue({
					card,
					elementDefs: getElementsForCard(item?.card_id),
					item,
					resolveLookupValue,
				});
				return { id, item, card, label, alreadyLinked: linkedSet.has(id) };
			})
			.filter(Boolean)
			.filter(row => {
				if (!needle) return true;
				return `${row.label} ${row.card?.name || ''}`.toLowerCase().includes(needle);
			});
	}, [items, excludedId, cardsById, getElementsForCard, resolveLookupValue, linkedSet, search]);

	const cardOptions = useMemo(() => {
		const seen = new Map();
		items.forEach(item => {
			const id = normalizeId(item?.card_id);
			if (!id || seen.has(id)) return;
			seen.set(id, cardsById[id]?.name || id);
		});
		// With a card filter active the fetch only returns that card's items,
		// so keep the selected option present even when the list is empty.
		if (cardFilter && !seen.has(cardFilter)) {
			seen.set(cardFilter, cardsById[cardFilter]?.name || cardFilter);
		}
		return Array.from(seen.entries())
			.map(([value, label]) => ({ value, label }))
			.sort((a, b) => `${a.label}`.localeCompare(`${b.label}`));
	}, [items, cardsById, cardFilter]);

	const truncated = totalCount > items.length;

	return (
		<Modal
			open={open}
			onCancel={onCancel}
			footer={null}
			title='Relate an item'
			width={720}
			centered
		>
			<div className='relate-item-modal__filters'>
				<Select
					className='relate-item-modal__card-filter'
					placeholder='All card types'
					allowClear
					value={cardFilter}
					onChange={value => setCardFilter(value || null)}
					options={cardOptions}
					showSearch
					filterOption={optionSearchFilter}
					disabled={loading}
				/>
				<Input
					{...NO_TEXT_ASSIST}
					className='relate-item-modal__search'
					placeholder='Search items'
					allowClear
					value={search}
					onChange={event => setSearch(event.target.value)}
				/>
			</div>

			{truncated ? (
				<Alert
					type='info'
					showIcon
					className='relate-item-modal__notice'
					message={`Showing the first ${items.length} of ${totalCount} items. Filter by card type to narrow the list.`}
				/>
			) : null}

			<DialogLoadState
				loaded={loaded}
				loading={loading}
				error={loadError}
				label='the items to relate'
				onRetry={() => setAttempt(n => n + 1)}
				skeleton={<Skeleton variant='list-row' count={6} />}
				minHeight={320}
			>
			<Spin spinning={saving}>
				{rows.length ? (
					<List
						className='relate-item-modal__list'
						split={false}
						dataSource={rows}
						rowKey={row => row.id}
						renderItem={row => (
							<List.Item
								className={row.alreadyLinked
									? 'relate-item-modal__row relate-item-modal__row--disabled'
									: 'relate-item-modal__row'}
								onClick={() => {
									if (row.alreadyLinked || saving) return;
									onSelect(row.id);
								}}
							>
								<RecordIdentity
									className='relate-item-modal__identity'
									badge={(
										<RecordBadge
											kind='item'
											record={row.card}
											size='sm'
											className='relate-item-modal__badge'
											title={row.label}
											// A parent's picker lists its family: a
											// sub-collection's record says where it lives.
											collection={itemHomeCollectionId(row.item)}
										/>
									)}
									below={(
										<span className='relate-item-modal__row-meta'>
											<span>{row.card?.name || 'Unknown card'}</span>
											{row.alreadyLinked ? <Tag>Already linked</Tag> : null}
										</span>
									)}
								/>
							</List.Item>
						)}
					/>
				) : (
					<Empty description='No other items in this collection to relate.' />
				)}
			</Spin>
			</DialogLoadState>
		</Modal>
	);
}
